import { LIFECYCLE_STATUS } from 'features/Sku/shared/SkuEnumHelpers';

/**
 * Maps a payout `OrderLineItem` to the PartnerOrder shape expected by `SkuOrderFlyout`.
 *
 * Item names are used as group keys (`itemSummary.itemId`) so that
 * `formatItemGroupsForFlyout` can group duplicate items by name and show a quantity.
 *
 * @param {import('./SkuPayoutReports.types').OrderLineItem} lineItem
 * @returns {object} PartnerOrder shape
 */
export const normalizeOrderLineItemToPartnerOrder = lineItem => {
  let lifecycleStatus;
  if (lineItem.cancellationType) {
    lifecycleStatus = LIFECYCLE_STATUS.CANCELLED;
  } else if (lineItem.markedFulfilledAt) {
    lifecycleStatus = LIFECYCLE_STATUS.FULFILLED;
  } else {
    lifecycleStatus = LIFECYCLE_STATUS.ACTIVE;
  }

  const userName = [lineItem.customerFirstName, lineItem.customerLastName].filter(Boolean).join(' ');

  const orderItems = (lineItem.items || []).map(item => ({
    core: {
      itemSummary: { itemId: item.name, name: item.name },
      cancelledAt: item.cancellationType ? lineItem.effectiveDate : null,
      cancellationType: item.cancellationType || null,
    },
    payableAmount: {
      amount: item.payableAmount ?? 0,
      currencyCode: lineItem.currencyCode,
    },
    basePayableAmount:
      item.basePayableAmount != null ? { amount: item.basePayableAmount, currencyCode: lineItem.currencyCode } : null,
  }));

  return {
    core: {
      orderId: lineItem.orderId,
      userId: lineItem.userId ?? null,
      markedFulfilledAt: lineItem.markedFulfilledAt || null,
      markedFulfilledType: lineItem.markedFulfilledType || null,
      userMarkedPickupAt: null,
    },
    userName,
    lifecycleStatus,
    payableAmount: {
      amount: lineItem.amountConverted,
      currencyCode: lineItem.currencyCode,
    },
    orderItems,
  };
};

/**
 * Maps a legacy payout `ReservationLineItem` to the PartnerOrder shape via a
 * synthetic one-item order (reservationId → orderId, className → item).
 *
 * @param {import('./SkuPayoutReports.types').ReservationLineItem} lineItem
 * @returns {object} PartnerOrder shape
 */
export const normalizeReservationLineItemToPartnerOrder = lineItem => {
  const syntheticOrderLineItem = {
    orderId: String(lineItem.reservationId),
    orderDateTime: lineItem.effectiveDate,
    items: lineItem.className
      ? [
          {
            name: lineItem.className,
            payableAmount: lineItem.amountConverted ?? 0,
            basePayableAmount: null,
            cancellationType: null,
          },
        ]
      : [],
    customerFirstName: lineItem.firstName,
    customerLastName: lineItem.lastName,
    customerEmail: null,
    status: lineItem.status,
    cancellationType: null,
    markedFulfilledAt: null,
    markedFulfilledType: null,
    effectiveDate: lineItem.effectiveDate,
    currencyCode: lineItem.currencyCode,
    amountConverted: lineItem.amountConverted,
    userId: null,
  };

  return normalizeOrderLineItemToPartnerOrder(syntheticOrderLineItem);
};
